import { useMemo } from "react";
import { useConversations } from "@/hooks/conversation/useConversations";
import type { Conversation as RealConversation } from "@/types/conversation/conversation";
import type {
  Conversation,
  ConversationChannel,
  ConversationOutcome,
} from "@/types/voice-vision";

/**
 * Owner-view conversation log for one genie.
 *
 * Composes the real {@link useConversations} infinite query for this single
 * genie over the last 30 days, flattens every loaded page, and maps each real
 * platform Conversation onto the calm view-model the Conversations tab renders.
 * Fields the platform cannot yet hand us (a true handed-off / lead-captured /
 * missed signal) are left at an honest default rather than fabricated, and the
 * summary stays blank while analysis is still catching up.
 */

const TIME_RANGE = "30d";

/** Phone vs website chat, derived from real call metadata. */
const deriveChannel = (conv: RealConversation): ConversationChannel =>
  conv.metadata?.phone_call ? "phone" : "web";

/**
 * Started-at as an ISO string: prefer the analysed conversation_date, otherwise
 * convert the raw unix-seconds start_time from call metadata.
 */
const deriveStartedAt = (conv: RealConversation): string => {
  const fromDetails = conv.details?.conversation_date;
  if (fromDetails) return fromDetails;

  const startTime = conv.metadata?.start_time;
  if (typeof startTime === "number" && Number.isFinite(startTime)) {
    return new Date(startTime * 1000).toISOString();
  }
  return "";
};

/**
 * Outcome heuristic derived from call metadata and analysis results.
 * Returns "resolved" when the call succeeded or completed, "lead-captured"
 * when analysis surfaced any lead_info field, and "missed" otherwise.
 */
const deriveOutcome = (conv: RealConversation): ConversationOutcome => {
  const successful = conv.metadata?.call_successful === "success";
  const complete = conv.details?.status === "complete";
  if (successful || complete) return "resolved";

  const leadInfo = conv.details?.analysis_result?.lead_info;
  if (leadInfo && Object.values(leadInfo).some(Boolean)) return "lead-captured";

  return "missed";
};

/** Caller label: lead phone/email if analysis surfaced one, else a calm fallback. */
const deriveCaller = (conv: RealConversation): string => {
  const lead = conv.details?.analysis_result?.lead_info ?? conv.details?.lead_info;
  const phone = lead?.phone ?? lead?.phone_number;
  const email = lead?.email;
  if (typeof phone === "string" && phone.trim()) return phone.trim();
  if (typeof email === "string" && email.trim()) return email.trim();
  return "Website visitor";
};

const toViewModel = (conv: RealConversation, genieId: string): Conversation => ({
  id: conv.id,
  genieId,
  caller: deriveCaller(conv),
  channel: deriveChannel(conv),
  summary: conv.details?.summary ?? "",
  topic: conv.details?.topics?.[0],
  outcome: deriveOutcome(conv),
  durationSec: conv.details?.duration ?? undefined,
  startedAt: deriveStartedAt(conv),
});

export const useGenieConversations = (id: string) => {
  const query = useConversations(id ? [id] : [], TIME_RANGE);

  const data = useMemo(() => {
    const flat = (query.data?.pages ?? []).flatMap((page) => page.data);
    return { data: flat.map((conv) => toViewModel(conv, id)) };
  }, [query.data, id]);

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
};
